import React, { useState } from 'react';
import { useSiteContent } from '../contexts/SiteContentContext';
import { Save, Layout, Type, Phone, Activity, FileText } from 'lucide-react';

export const SiteEditor: React.FC = () => {
  const { content, setContent } = useSiteContent();
  
  const [formData, setFormData] = useState({
    heroPrimaryHook: content.heroPrimaryHook,
    heroSubHook: content.heroSubHook,
    globalSupportNumber: content.globalSupportNumber,
    targetDirective: content.targetDirective,
    statusIndicator: content.statusIndicator,
  });

  const [isSaved, setIsSaved] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setIsSaved(false);
  };

  const handleSave = () => {
    setContent(prev => ({
      ...prev,
      ...formData
    }));
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  return (
    <div className="flex flex-col h-full space-y-6 max-w-4xl mx-auto w-full pb-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end border-b border-cyan-500/20 pb-6 mb-2 gap-4 shrink-0 mt-8">
        <div>
          <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest block mb-2">INTEGRATION NODE 12: ACTIVE</span>
          <h2 className="text-2xl font-bold text-white uppercase tracking-widest font-sans drop-shadow-md flex items-center gap-3">
            <Layout className="w-6 h-6 text-cyan-400 shrink-0" />
            WEBSITE SITE EDITOR
          </h2>
        </div>
        
        <div className="flex items-center gap-3 shrink-0">
          <button 
            onClick={handleSave}
            className={`flex items-center justify-center gap-2 ${isSaved ? 'bg-emerald-600/80 border-emerald-400/50' : 'bg-gradient-to-r from-cyan-600/80 to-blue-800/80 hover:from-cyan-500 hover:to-blue-700 border-cyan-400/50'} border transition-all px-6 py-2.5 rounded shadow-[0_0_15px_rgba(34,211,238,0.3)] text-white group`}
          >
            <Save className="w-4 h-4 shadow-[0_0_10px_rgba(255,255,255,0.5)] bg-white/10 rounded" />
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] font-bold">
              {isSaved ? 'PAYLOAD SECURED' : 'DEPLOY CHANGES'}
            </span>
          </button>
        </div>
      </div>
      
      <div className="flex-1 flex flex-col gap-6">
        <div className="bg-[#050914]/80 backdrop-blur-md border border-slate-700/50 rounded-2xl shadow-lg overflow-hidden">
          <div className="h-12 border-b border-white/5 bg-black/40 flex items-center px-6">
            <h3 className="font-mono text-[11px] text-cyan-400 uppercase tracking-widest font-bold flex items-center gap-2">
              <Type className="w-3 h-3" />
              GLOBAL METADATA EXPORT
            </h3>
          </div>
          <div className="p-6 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3 relative group">
                <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Hero Primary Hook</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Layout className="h-4 w-4 text-slate-500" />
                  </div>
                  <input 
                    type="text" 
                    name="heroPrimaryHook"
                    value={formData.heroPrimaryHook}
                    onChange={handleChange}
                    className="w-full bg-[#0b0f19] border border-slate-700 focus:border-cyan-500 rounded text-white font-sans text-sm pl-10 px-4 py-3 outline-none transition-colors shadow-inner"
                  />
                </div>
              </div>

              <div className="space-y-3 relative group">
                <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Hero Sub Hook</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FileText className="h-4 w-4 text-slate-500" />
                  </div>
                  <input 
                    type="text" 
                    name="heroSubHook"
                    value={formData.heroSubHook}
                    onChange={handleChange}
                    className="w-full bg-[#0b0f19] border border-slate-700 focus:border-cyan-500 rounded text-white font-sans text-sm pl-10 px-4 py-3 outline-none transition-colors shadow-inner"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3 relative group">
                <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Global Support Line (VoIP)</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-4 w-4 text-slate-500" />
                  </div>
                  <input 
                    type="text" 
                    name="globalSupportNumber"
                    value={formData.globalSupportNumber}
                    onChange={handleChange}
                    className="w-full bg-[#0b0f19] border border-slate-700 focus:border-cyan-500 rounded text-white font-sans text-sm pl-10 px-4 py-3 outline-none transition-colors shadow-inner font-mono tracking-widest"
                  />
                </div>
              </div>

              <div className="space-y-3 relative group">
                <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">System Status Indicator</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Activity className="h-4 w-4 text-slate-500" />
                  </div>
                  <select 
                    name="statusIndicator"
                    value={formData.statusIndicator}
                    onChange={handleChange}
                    className="w-full bg-[#0b0f19] border border-slate-700 focus:border-cyan-500 rounded text-white font-sans text-sm pl-10 px-4 py-3 outline-none transition-colors shadow-inner appearance-none cursor-pointer"
                  >
                    <option value="all-nominal">ALL SYSTEMS NOMINAL</option>
                    <option value="maintenance">MAINTENANCE CYCLE</option>
                    <option value="breach">SECURITY BREACH DETECTED</option>
                    <option value="extraction">ACTIVE EXTRACTION PROTOCOL</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="space-y-3 relative group">
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Target Directive Module</label>
              <textarea 
                name="targetDirective"
                value={formData.targetDirective}
                onChange={handleChange}
                rows={4}
                className="w-full bg-[#0b0f19] border border-slate-700 focus:border-cyan-500 rounded text-amber-500/80 font-mono text-xs p-4 outline-none transition-colors shadow-inner leading-relaxed"
              />
            </div>
          </div>
        </div>
        
        <div className="bg-black/60 border border-red-500/30 rounded-2xl p-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-red-900/10 pointer-events-none animate-pulse"></div>
          <h3 className="font-mono text-[11px] text-red-500 uppercase tracking-widest font-bold flex items-center gap-2 mb-2 relative z-10">
             ⚠️ OVERRIDE WARNING
          </h3>
          <p className="font-mono text-[9px] text-slate-400 uppercase tracking-wider leading-relaxed relative z-10 max-w-2xl">
            Modifying these parameters alters the global frontend node architecture. Submitting changes broadcasts states across all connected client interfaces instantly. Revisions are permanent and stored in the Holographic Database array.
          </p>
        </div>

      </div>
    </div>
  );
};
